import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { DemoProvider } from "@/components/demo/DemoModeManager";
import { PresentationModeProvider } from "@/contexts/PresentationModeContext";
import { AuthProvider } from "@/contexts/AuthContext";
import PresentationModeController from "@/components/PresentationModeController";
import ConsentBanner from "@/components/ConsentBanner";
import ConsentScriptsGate from "@/components/ConsentScriptsGate";
import MarketingLocaleRoutes from "@/components/MarketingLocaleRoutes";
import GAPageViewTracker from "@/hooks/useGAPageView";
import { SUPPORTED_LOCALES, SupportedLocale } from "@/i18n/index";

// Dev-only untranslated-text overlay (excluded from production builds).
const I18nDevOverlay = import.meta.env.DEV
  ? React.lazy(() => import("@/dev/I18nDevOverlay"))
  : null;

// Create QueryClient for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
    },
  },
});

const DEFAULT_LOCALE: SupportedLocale = 'en-US';

/**
 * Locales this site used to serve, mapped to the one that now carries the same
 * language. en-GB, en-SG, en-ZA and fr-CA were retired on Aug 30 2026 ("Five
 * languages, no country variants"); zh-CN and pt-BR went earlier. Every one of
 * them is still in Google's index, so the URLs keep arriving.
 *
 * Language is preserved where there is somewhere to preserve it to: fr-CA goes
 * to fr-FR rather than dumping a French speaker on the English site.
 */
const RETIRED_LOCALES: Record<string, SupportedLocale> = {
  'en-GB': 'en-US',
  'en-SG': 'en-US',
  'en-ZA': 'en-US',
  'fr-CA': 'fr-FR',
  'zh-CN': 'en-US',
  'pt-BR': 'en-US',
};

/** Anything shaped like a locale segment, whether or not we ever served it. */
const LOCALE_SHAPED = /^[a-z]{2}-[A-Z]{2}$/;

/**
 * Unprefixed paths go to the reader's remembered locale: /pricing → /fr-FR/pricing.
 *
 * The subtlety, and the source of a run of 404s: this used to prepend the
 * remembered locale to whatever arrived, unconditionally. A retired prefix from
 * Google's index matched no locale route, fell through to here, and came out as
 * /fr-FR/en-GB/pricing — the reader's locale stacked on top of the stale one.
 * Both segments present, nothing to route to, 404.
 *
 * So a leading locale-shaped segment is now resolved instead of buried: mapped
 * when we know it, dropped when we do not. Dropping is the right default for an
 * unknown xx-XX, because the rest of the path is still a real page and the
 * reader gets it in their own language rather than a 404.
 */
/**
 * Paths that belong to the application at app.csquaregrc.com, not to this site.
 *
 * public/_redirects has 301 rules for all of these, and that file is not
 * honoured by the host currently serving csquaregrc.com: verified on Sep 4 by
 * requesting /dashboard, /profile and /admin, which should have left the origin
 * and instead landed on /en-US/compliance, /en-US/profile and /en-US/admin —
 * the last two rendering Page Not Found. Anyone following an old link, a
 * bookmark or an email into the app hit a 404 on the marketing site.
 *
 * Handling it here means it works wherever the site is hosted, rather than
 * depending on edge config that may or may not be read. If the host later does
 * honour _redirects, the edge wins and this never runs.
 */
const APP_PATHS = new Set([
  'dashboard',
  'dashboard-compliancecore',
  'profile',
  'admin',
  'task-requests',
  'workflows',
  'program-execution',
  'auditor-portal',
  'agent',
  'workflow-builder',
  'workflow-templates',
  'program-analytics',
  'knowledge-base',
  'subscription',
]);

const APP_ORIGIN = 'https://app.csquaregrc.com';

/** Strip a locale prefix so /en-US/admin resolves the same as /admin. */
const withoutLocale = (segments: string[]) =>
  segments.length > 0 && LOCALE_SHAPED.test(segments[0]) ? segments.slice(1) : segments;

const LocaleFallbackRedirect = () => {
  const location = useLocation();
  const remembered = localStorage.getItem('preferred-locale') as SupportedLocale | null;
  const preferred = remembered && SUPPORTED_LOCALES.includes(remembered) ? remembered : DEFAULT_LOCALE;

  const segments = location.pathname.split('/').filter(Boolean);
  const first = segments[0];

  // App surfaces leave the origin entirely. Checked before the locale fallback,
  // which would otherwise rewrite /admin to /en-US/admin and 404 it.
  const appSegments = withoutLocale(segments);
  if (appSegments.length > 0 && APP_PATHS.has(appSegments[0])) {
    const target = `${APP_ORIGIN}/${appSegments.join('/')}${location.search}${location.hash}`;
    window.location.replace(target);
    return null;
  }

  let locale = preferred;
  let rest = segments;

  if (first && LOCALE_SHAPED.test(first)) {
    // A retired locale is authoritative about language; an unknown one is not,
    // so it defers to whatever the reader already had.
    locale = RETIRED_LOCALES[first] ?? preferred;
    rest = segments.slice(1);
  }

  const path = rest.length > 0 ? `/${rest.join('/')}` : '';
  return <Navigate to={`/${locale}${path}${location.search}${location.hash}`} replace />;
};

function AppMarketing() {
  const [consentGiven, setConsentGiven] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true,
    functional: false,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const existingConsent = localStorage.getItem('cookie-consent');
    if (existingConsent) {
      try {
        const parsed = JSON.parse(existingConsent);
        setConsentGiven(true);
        setPreferences(parsed);
      } catch (error) {
        console.warn('Failed to parse existing consent:', error);
      }
    }

    const handleConsentChange = (event: CustomEvent) => {
      setConsentGiven(true);
      setPreferences(event.detail);
    };

    window.addEventListener('consentUpdated', handleConsentChange as EventListener);
    return () => window.removeEventListener('consentUpdated', handleConsentChange as EventListener);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <PresentationModeProvider>
          <DemoProvider>
            <BrowserRouter>
              <GAPageViewTracker />
              <ConsentBanner />
              <ConsentScriptsGate />
              <Toaster />
              <PresentationModeController />
              {I18nDevOverlay && (
                <React.Suspense fallback={null}>
                  <I18nDevOverlay />
                </React.Suspense>
              )}
              <Routes>
                {/* Locale-prefixed routes: /:locale/* */}
                {SUPPORTED_LOCALES.map((locale) => (
                  <Route
                    key={locale}
                    path={`/${locale}/*`}
                    element={<MarketingLocaleRoutes locale={locale} />}
                  />
                ))}

                {/* Retired locale prefixes and unprefixed paths both land here.
                    RETIRED_LOCALES maps the ones we used to serve; anything else
                    shaped like xx-XX is dropped rather than stacked. */}
                <Route path="/*" element={<LocaleFallbackRedirect />} />
              </Routes>
            </BrowserRouter>
          </DemoProvider>
        </PresentationModeProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default AppMarketing;
